// The workspace's one search. The sidebar's box writes it; the overview on
// an empty board reads it; so both show what the same sessions used.
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useQuery } from "./dataClient.js";
import { useFlaggedReach } from "./flaggedReach.js";
import { QUEUE_SECTIONS, queuePlace, reviewPlaces, useFleetFindings } from "./fleetFindings.js";
import { pipelineFilterText } from "./queryPipeline.js";
import { flaggedOnly, splitReview } from "./reviewClause.js";
import { SESSION_MATCH_CAP, sessionClauses } from "./sessionMatch.js";

let text = "";
// The search as a click last set it (a value picked on the board, a clause
// taken off), which its readers take up at once; typed, it waits for the
// typing to stop.
let atOnce = null;
const subscribers = new Set();

export function setWorkspaceSearch(next, { now = false } = {}) {
  const value = typeof next === "function" ? next(text) : next;
  if (value === text) return;
  text = String(value ?? "");
  atOnce = now ? text : null;
  for (const fn of subscribers) fn();
}

/** Whether the search `t` was set by a click, to be read without waiting. */
export function searchSetAtOnce(t) {
  return atOnce != null && atOnce === t;
}

const subscribe = (fn) => {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
};
const read = () => text;

export function useWorkspaceSearch() {
  return [useSyncExternalStore(subscribe, read, read), setWorkspaceSearch];
}

// The sessions picked into the search box one by one ({ id, title }), to
// start a workspace with: held beside the search's clauses rather than in
// them, so picking one does not narrow what the search reaches (and the
// list it was picked from stays as it was, to pick more).
let picks = [];
const pickSubscribers = new Set();
export function setWorkspacePicks(next) {
  const value = typeof next === "function" ? next(picks) : next;
  if (value === picks) return;
  picks = Array.isArray(value) ? value : [];
  for (const fn of pickSubscribers) fn();
}
const subscribePicks = (fn) => {
  pickSubscribers.add(fn);
  return () => pickSubscribers.delete(fn);
};
const readPicks = () => picks;
export function useWorkspacePicks() {
  return [useSyncExternalStore(subscribePicks, readPicks, readPicks), setWorkspacePicks];
}
// A session in, or out if it is in; and many in at once (those not in yet).
export const togglePick = (item) => setWorkspacePicks((list) => (list.some((x) => x.id === item.id) ? list.filter((x) => x.id !== item.id) : [...list, { id: item.id, title: item.title || "" }]));
export const addPicks = (items) => setWorkspacePicks((list) => {
  const have = new Set(list.map((x) => x.id));
  const more = items.filter((x) => x?.id && !have.has(x.id)).map((x) => ({ id: x.id, title: x.title || "" }));
  return more.length ? [...list, ...more] : list;
});

const NO_SCOPE = Object.freeze([]);
const NO_MATCH = Object.freeze({ sessions: Object.freeze([]), clauses: Object.freeze([]), capped: false, prefiltered: false });

// The sessions a search matches (getSessionMatches), its review clauses
// (lib/reviewClause.js) applied to them from where the range's findings left
// each session. Clauses only flagged sessions pass are asked of the findings'
// own sessions, every one in the range (lib/flaggedReach.js, the read the
// page's counts share, so drilling to a place asks nothing more), and the
// newest SESSION_MATCH_CAP of them kept, as the match keeps; one passing
// sessions with nothing flagged, of the newest the rest of the search
// matches. `scopePath` narrows the match to a list of sessions, as the
// match's own does.
export function useSessionMatch(range, q, { scopePath = NO_SCOPE, enabled = true } = {}) {
  const { rest, keeps } = useMemo(() => splitReview(q), [q]);
  const flagged = flaggedOnly(keeps);
  const fleet = useFleetFindings(range, { enabled: enabled && !!keeps });
  const places = useMemo(() => (keeps && fleet.status === "done" ? reviewPlaces(fleet.findings) : null), [keeps, fleet.status, fleet.findings]);
  const ids = useMemo(() => {
    if (!flagged || !places) return null;
    const within = scopePath.find((s) => s.dim === "sessions")?.ids;
    const inScope = within ? new Set(within.map((id) => String(id).toLowerCase())) : null;
    return [...places].filter(([id, place]) => keeps(place) && (!inScope || inScope.has(id))).map(([id]) => id);
  }, [flagged, keeps, places, scopePath]);
  const reach = useFlaggedReach(range, rest, ids?.length ? ids : null, { enabled });
  const match = useQuery("getSessionMatches", { scopePath, range, query: rest }, { enabled: enabled && !flagged && (!keeps || !!places) });
  // Kept while what it is made of holds still: the boards read from it lay
  // out again whenever its sessions are a new list.
  const kept = useMemo(() => {
    if (!keeps) return null;
    if (fleet.status === "error") return { data: undefined, loading: false, error: fleet.error || new Error("The review could not be read") };
    if (!places) return { data: undefined, loading: enabled, error: null };
    if (flagged) {
      if (!ids.length) return { data: NO_MATCH, loading: false, error: null };
      if (reach.status === "error") return { data: undefined, loading: false, error: reach.error };
      if (reach.status !== "done") return { data: undefined, loading: true, error: null };
      const all = ids.map((id) => reach.reached.get(String(id).toLowerCase())).filter(Boolean).sort(newestFirst);
      return { data: { ...reach.meta, sessions: all.slice(0, SESSION_MATCH_CAP), capped: all.length > SESSION_MATCH_CAP }, loading: false, error: null };
    }
    if (!match.data) return { data: undefined, loading: match.loading, error: match.error };
    return { data: { ...match.data, sessions: match.data.sessions.filter((s) => keeps(places.get(String(s.id).toLowerCase()) || "none")) }, loading: match.loading, error: match.error };
  }, [keeps, flagged, ids, reach, match.data, match.loading, match.error, fleet.status, fleet.error, places, enabled]);
  return kept || match;
}
// The match's own order: the newest activity first.
const newestFirst = (a, b) => (Number(b.lastMs) || 0) - (Number(a.lastMs) || 0) || String(a.id).localeCompare(String(b.id));
// Where review left every session a search reaches, not only the newest a
// board is drawn from: how many at each flagged place (useReviewedInReach).
// Null until read, or while not `enabled`.
export function useReviewReach(range, q, { enabled = true } = {}) {
  const reviewed = useReviewedInReach(range, q, { enabled });
  return useMemo(() => {
    if (reviewed.status !== "done") return null;
    const counts = { met: 0, not_verified: 0, not_met: 0, set_aside: 0 };
    for (const place of reviewed.places.values()) if (place in counts) counts[place] += 1;
    return counts;
  }, [reviewed]);
}

// The reviewed sessions a search reaches, each with where review left it:
// `places`, session id -> place, once `status` is "done", and when they were
// active (`firstMs`, `lastMs`, as the match has it); `rows`, each as the match
// has it (what the search found in it, against `clauses`). The flagged
// sessions are the range's findings' (reviewPlaces), kept only where the
// session match reaches them, as a click on a place lists them: a session
// the search reaches, active in the range (one reviewed in the range but not
// active in it is not). Read through lib/flaggedReach.js, which the drill
// shares. "loading" while not `enabled`.
export function useReviewedInReach(range, q, { enabled = true } = {}) {
  const fleet = useFleetFindings(range, { enabled });
  const places = useMemo(() => (fleet.status === "done" ? reviewPlaces(fleet.findings) : null), [fleet.status, fleet.findings]);
  // The search as the match asks it; its review clauses keep places here.
  const { rest, keeps } = useMemo(() => splitReview(appendClause(q || "", "-review:none")), [q]);
  const ids = useMemo(() => (places ? [...places].filter(([, place]) => keeps(place)).map(([id]) => id) : null), [places, keeps]);
  const reach = useFlaggedReach(range, rest, ids?.length ? ids : null, { enabled });
  return useMemo(() => {
    if (!enabled) return NOT_REVIEWED;
    if (fleet.status === "error") return { status: "error", places: null };
    if (!ids) return NOT_REVIEWED;
    const out = new Map();
    if (!ids.length) return { status: "done", places: out, firstMs: null, lastMs: null };
    if (reach.status === "error") return { status: "error", places: null };
    if (reach.status !== "done") return NOT_REVIEWED;
    let firstMs = null;
    let lastMs = null;
    for (const id of ids) {
      const key = String(id).toLowerCase();
      const s = reach.reached.get(key);
      if (!s) continue;
      out.set(key, places.get(id));
      const a = Number(s.firstMs ?? s.lastMs);
      const b = Number(s.lastMs);
      if (Number.isFinite(a) && (firstMs == null || a < firstMs)) firstMs = a;
      if (Number.isFinite(b) && (lastMs == null || b > lastMs)) lastMs = b;
    }
    return { status: "done", places: out, firstMs, lastMs, rows: reach.reached, clauses: reach.meta?.clauses || [] };
  }, [enabled, fleet.status, ids, reach, places]);
}
const NOT_REVIEWED = Object.freeze({ status: "loading", places: null });

const SEVERITY_ORDER = new Map(["critical", "high", "medium", "low"].map((id, i) => [id, i]));
const severityRank = (sev) => SEVERITY_ORDER.get(sev) ?? SEVERITY_ORDER.size;
const QUEUE_ORDER = new Map(QUEUE_SECTIONS.map(([id], i) => [id, i]));
const NO_ITEMS = Object.freeze([]);
// The flagged sessions a search reaches, each as a list shows it: how bad
// its worst finding is, how many it has, where review left it, when it was
// last flagged and what its findings say; under where review left each,
// newest flagged first. Every one the search reaches (useReviewedInReach),
// as the board counts them, not only the newest; each with its `row` as the
// match has it, against `clauses`. `flags`: every flagged session of the
// range by id, for a session's row on any list.
export function useFlaggedInReach(range, q, { enabled = true } = {}) {
  const reviewed = useReviewedInReach(range, q, { enabled });
  const fleet = useFleetFindings(range, { enabled });
  const flags = useMemo(() => {
    const by = new Map();
    for (const f of fleet.findings) {
      if (!f.session) continue;
      if (!by.has(f.session)) by.set(f.session, []);
      by.get(f.session).push(f);
    }
    return new Map([...by].map(([id, list]) => {
      const sorted = [...list].sort((a, b) => severityRank(a.severity) - severityRank(b.severity));
      const met = [...new Set(list.flatMap((f) => f.met))];
      const worst = sorted[0]?.severity;
      return [id, {
        sev: SEVERITY_ORDER.has(worst) ? worst : null,
        findings: list.length,
        place: queuePlace(list),
        at: Math.max(0, ...list.map((f) => f.at || 0)) || null,
        endpointId: list.find((f) => f.endpoint)?.endpoint || "",
        note: met.length ? `Met ${met.join(", ")}` : sorted.find((f) => f.title)?.title || "",
      }];
    }));
  }, [fleet.findings]);
  return useMemo(() => {
    if (reviewed.status === "error") return { status: "error", items: NO_ITEMS, flags, clauses: NO_ITEMS };
    if (reviewed.status !== "done") return { status: "loading", items: NO_ITEMS, flags, clauses: NO_ITEMS };
    const items = [...reviewed.places.keys()].map((id) => {
      const flag = flags.get(id);
      return { id, sev: flag?.sev ?? null, findings: flag?.findings ?? 0, lastMs: flag?.at ?? null, endpointId: flag?.endpointId || "", place: flag?.place || "open", note: flag?.note || "", row: reviewed.rows?.get(id) || null };
    }).sort((a, b) => QUEUE_ORDER.get(a.place) - QUEUE_ORDER.get(b.place) || (b.lastMs || 0) - (a.lastMs || 0));
    return { status: "done", items, flags, clauses: reviewed.clauses || NO_ITEMS };
  }, [reviewed, flags]);
}

// Review clauses no session passes.
const NO_SCOPE_LEFT = Object.freeze([]);
// A session id none has: what reads are scoped to when a search keeps none.
const NO_SESSION = "00000000-0000-0000-0000-000000000000";
const NOTHING_REACHED = Object.freeze([Object.freeze({ dim: "sessions", ids: Object.freeze([NO_SESSION]), id: NO_SESSION })]);

// What the search reaches, as a read's scope: with no search, the range;
// under the match's cap, the sessions it matches (every request of each);
// past the cap, the requests the search itself matches. Review clauses are
// read in the browser, so with them the scope is the sessions they keep
// whatever the cap, and keeping none, a session none has. `ready` waits for
// the match, so nothing is read twice.
export function useSearchScope(range, { delayMs = 400 } = {}) {
  const [live] = useWorkspaceSearch();
  const [q, setQ] = useState(() => pipelineFilterText(live));
  useEffect(() => {
    if (searchSetAtOnce(live)) {
      setQ(pipelineFilterText(live));
      return undefined;
    }
    const t = setTimeout(() => setQ(pipelineFilterText(live)), delayMs);
    return () => clearTimeout(t);
  }, [live, delayMs]);
  const match = useSessionMatch(range, q, { enabled: !!q });
  const reviewed = useMemo(() => !!splitReview(q).keeps, [q]);
  // The same object for as long as what it says holds, so what is handed it
  // (the page's sections) is not drawn again only for the page's sake: the
  // match's own object is new at each draw.
  const { data, loading, error } = match;
  return useMemo(() => {
    const read = { data, loading, error };
    if (!q) return { q, ready: true, scopePath: NO_SCOPE, steer: "", match: read, capped: false, sessions: null };
    const sessions = data?.sessions || null;
    const capped = !!data?.capped;
    const whole = !!sessions?.length && (!capped || reviewed);
    return {
      q,
      ready: !!data,
      scopePath: whole ? [{ dim: "sessions", ids: sessions.map((x) => x.id), id: sessions[0].id }] : reviewed && sessions ? NOTHING_REACHED : NO_SCOPE,
      steer: whole || reviewed ? "" : q,
      match: read,
      capped,
      sessions,
    };
  }, [q, data, loading, error, reviewed]);
}

// Whether a search already holds a clause (each of its parts, as the search
// reads them): narrowing to it again would say nothing new. Review clauses
// are read as lib/reviewClause.js reads them.
const clauseKeys = (text) => [
  ...sessionClauses(text).clauses.map((c) => `${c.negated ? "-" : ""}${c.text}`),
  ...splitReview(text).tokens.map((k) => `${k.negated ? "-" : ""}${k.text}`),
];
export function holdsClause(current, clause) {
  if (!clause) return false;
  const have = new Set(clauseKeys(pipelineFilterText(String(current ?? ""))));
  const want = clauseKeys(clause);
  return want.length > 0 && want.every((k) => have.has(k));
}

// A clause added to a search: before any aggregate (`|>`), so the pipeline
// keeps running over what the search now matches. One it holds already is
// not added again.
export function appendClause(current, clause) {
  const t = String(current ?? "");
  if (holdsClause(t, clause)) return t;
  const at = t.indexOf("|>");
  const head = (at >= 0 ? t.slice(0, at) : t).trim();
  const tail = at >= 0 ? ` ${t.slice(at)}` : "";
  return `${head ? `${head} ${clause}` : clause}${tail}`;
}
